// components/DailyHydrationCard.jsx
// =============================================================================
// THE one daily hydration card, rendered by BOTH My Hydration and WTR BTL.
// One component means the two screens cannot drift apart visually or in math.
//
// UNITS: ounce-primary for the US market, with a small liter reference under
// the big number, matching My Hydration. Callers still pass mL (dayMl, goalMl);
// the card does the conversion so there is one place to change it. Pass
// units="l" to flip the primary unit to liters.
//
// Data contract: callers pass the verified backend total (dayMl) and the NHS
// predicted target (goalMl), the same two sources on every screen.
//
// TWO QUANTITIES, NAMED (2026-07-11, the 57 vs 58 incident): dayMl is
// ABSORBED hydration (intake through the absorption efficiency model);
// verifiedMl is RAW intake the bottle measured. Raw verified can therefore
// legitimately exceed the absorbed headline. The card now labels both so
// the two numbers stop reading as a contradiction: the headline carries an
// "absorbed" tag and the green line says "intake verified". Do not "fix"
// the difference by displaying one quantity as the other.
// =============================================================================

const C = {
  navy: "#0F1419",
  textSecondary: "#3D4043",
  muted: "#A6A8AB",
  blue: "#51B0E6",
  blueDark: "#2B8FC9",
  green: "#4A8A6F",
  greenDark: "#3D7058",
  attention: "#C89B3C",
};

const OZ_PER_L = 33.814;
const toOz = (ml) => Math.round(((ml || 0) / 1000) * OZ_PER_L);
const toLStr = (ml) => ((ml || 0) / 1000).toFixed(1);

// Primary display value + unit, and the small companion reference string.
function fmtPrimary(ml, units) {
  if (units === "l") return { val: toLStr(ml), unit: "L" };
  return { val: String(toOz(ml)), unit: "oz" };
}
function fmtRef(ml, units) {
  if (units === "l") return toOz(ml) + " oz";
  return toLStr(ml) + " L";
}

function Ring({ pct = 0, dayMl = 0, units = "oz" }) {
  const SIZE = 130, R = 50;
  const circ = 2 * Math.PI * R;
  const fill = (Math.min(100, pct) / 100) * circ;
  const isDone = pct >= 100;
  const col = isDone ? C.green : C.blue;
  const p = fmtPrimary(dayMl, units);
  return (
    <div style={{ position: "relative", width: SIZE, height: SIZE, flexShrink: 0 }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="rgba(15, 20, 25, 0.06)" strokeWidth="9" />
        <circle
          cx={SIZE / 2} cy={SIZE / 2} r={R}
          fill="none" stroke={col} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${fill} ${circ}`}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.34,1.56,0.64,1), stroke 0.6s ease" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: col }}>{pct}%</div>
        <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{p.val} {p.unit}</div>
      </div>
    </div>
  );
}

export default function DailyHydrationCard({
  label = "Daily Hydration",
  dayMl = 0,
  goalMl = 2000,
  verifiedMl = null, // optional: bottle-verified portion of dayMl. Drives the
                     // verified/logged split and is the ONLY value the "Live
                     // from your bottle" chip may truthfully represent.
  live = false,
  units = "oz",     // "oz" (default, US) or "l"
  pace = null,      // optional { tone: "good" | "behind", text: string }
  hint = null,      // optional small centered hint (e.g. pair-your-bottle prompt)
  footnote = null,  // optional muted explainer under the card body
}) {
  const pct = goalMl > 0 ? Math.min(100, Math.round((dayMl / goalMl) * 100)) : 0;
  const done = pct >= 100;
  const paceColor = pace ? (pace.tone === "good" ? C.green : C.attention) : null;

  const day = fmtPrimary(dayMl, units);
  const goal = fmtPrimary(goalMl, units);
  const dayRef = fmtRef(dayMl, units);
  const goalRef = fmtRef(goalMl, units);

  return (
    <div className="card-default" style={{ padding: "18px 20px" }}>
      {/* Header with live chip */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase" }}>
          {label}
        </span>
        {live && (
          <span style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(74,138,111,0.10)", border: "1px solid rgba(74,138,111,0.20)", borderRadius: 20, padding: "3px 9px" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, animation: "dhcBlink 1.4s ease-in-out infinite" }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: C.greenDark }}>Live from your bottle</span>
          </span>
        )}
      </div>
      <style>{`@keyframes dhcBlink { 0%,100%{opacity:1} 50%{opacity:0.35} }`}</style>

      {/* Ring + numbers */}
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <Ring pct={pct} dayMl={dayMl} units={units} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 2 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: C.navy, fontVariantNumeric: "tabular-nums", letterSpacing: -1 }}>
              {day.val}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{day.unit}</span>
            <span style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>/ {goal.val} {goal.unit}</span>
            {/* Quantity tag: the headline is ABSORBED hydration (NHS model),
                not raw intake. Absorption, not just intake. */}
            <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: 0.5, textTransform: "uppercase", marginLeft: 2 }}>
              absorbed
            </span>
          </div>
          {/* small liter reference under the ounce line */}
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginBottom: 8 }}>
            {dayRef} / {goalRef}
          </div>
          <div style={{ fontSize: 12, color: done ? C.green : C.textSecondary, fontWeight: 500, marginBottom: 10 }}>
            {done ? "\u2713 Daily goal reached!" : `${pct}% of daily goal`}
          </div>
          <div style={{ height: 6, background: "rgba(15, 20, 25, 0.06)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${pct}%`, borderRadius: 3,
              background: done ? `linear-gradient(90deg, ${C.green}, #5BA882)` : `linear-gradient(90deg, ${C.blue}, ${C.blueDark})`,
              transition: "width 1s cubic-bezier(0.34,1.56,0.64,1)",
            }} />
          </div>
        </div>
      </div>

      {/* Verified vs logged split. Green is RAW intake the hardware measured
          (Trust But Verify); it is a different quantity from the absorbed
          headline and may read slightly higher. Muted is self-reported. */}
      {verifiedMl != null && (
        <div style={{ display: "flex", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.green }}>
            {toOz(verifiedMl)} oz intake verified by your bottle
          </span>
          <span style={{ fontSize: 11, color: C.muted }}>
            {toOz(Math.max(0, dayMl - verifiedMl))} oz logged
          </span>
        </div>
      )}

      {/* Predictive pace insight */}
      {pace && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "11px 13px", background: `${paceColor}10`, borderRadius: 12, border: `1px solid ${paceColor}25`, marginTop: 14 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: paceColor, marginTop: 5, flexShrink: 0 }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: C.textSecondary, lineHeight: 1.5 }}>{pace.text}</div>
        </div>
      )}

      {/* Footnote */}
      {footnote && (
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginTop: 12 }}>{footnote}</div>
      )}

      {/* Hint */}
      {hint && (
        <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>{hint}</div>
      )}
    </div>
  );
}
